import type { DbExecutor } from "../executor";
import type { ActivationSnapshot } from "../../engine/activation";

/**
 * THE FACTS BEHIND "WHAT NEXT?" (spec §86).
 *
 * Reads, for every project at once, exactly what the activation engine needs
 * — counts and earliest timestamps — in a FIXED number of statements however
 * many projects, rooms or lines exist. It writes nothing and stores nothing:
 * progress is derived on every read, which is what lets a user close the
 * browser mid-project and resume tomorrow with the app agreeing with the
 * data.
 *
 * "The plan the estimate follows" means what every estimate view already
 * means: common project scope (design_id IS NULL) plus the ACTIVE design's
 * own scope. Work in a design nobody made active is a scenario, not the
 * budget.
 */
export class ActivationRepository {
  constructor(private readonly db: DbExecutor) {}

  async snapshots(projectIds?: readonly string[]): Promise<ActivationSnapshot[]> {
    const projects = await this.db.select<{
      id: string;
      name: string;
      created_at: string;
      updated_at: string;
      street_address: string | null;
      purchase_price_cents: number | null;
      living_area_sqft_x100: number | null;
    }>(
      `SELECT id, name, created_at, updated_at, street_address, purchase_price_cents, living_area_sqft_x100
         FROM projects ORDER BY updated_at DESC`,
    );
    const wanted = projectIds ? new Set(projectIds) : null;
    const rows = wanted ? projects.filter((p) => wanted.has(p.id)) : projects;
    if (rows.length === 0) return [];

    const byProject = <T extends { project_id: string }>(list: T[]) => new Map(list.map((r) => [r.project_id, r]));

    const rooms = byProject(
      await this.db.select<{ project_id: string; n: number; measured: number; first_at: string | null }>(
        `SELECT f.project_id,
                COUNT(r.id) AS n,
                SUM(CASE WHEN r.floor_area_x100 IS NOT NULL
                          OR EXISTS (SELECT 1 FROM design_room_geometry g
                                      JOIN projects p ON p.id = f.project_id
                                     WHERE g.room_id = r.id AND g.design_id = p.active_design_id
                                       AND g.status = 'bound' AND g.floor_area_x100 IS NOT NULL)
                         THEN 1 ELSE 0 END) AS measured,
                MIN(r.created_at) AS first_at
           FROM rooms r JOIN floors f ON f.id = r.floor_id
          WHERE r.archived_at IS NULL AND f.archived_at IS NULL
          GROUP BY f.project_id`,
      ),
    );

    const walls = byProject(
      await this.db.select<{ project_id: string; n: number; first_at: string | null }>(
        `SELECT d.project_id, COUNT(w.id) AS n, MIN(w.created_at) AS first_at
           FROM walls w JOIN designs d ON d.id = w.design_id
          WHERE w.archived_at IS NULL AND d.archived_at IS NULL AND w.kind = 'wall'
          GROUP BY d.project_id`,
      ),
    );

    const scope = byProject(
      await this.db.select<{ project_id: string; n: number; first_at: string | null }>(
        `SELECT f.project_id, COUNT(f.id) AS n, MIN(f.created_at) AS first_at
           FROM project_features f JOIN projects p ON p.id = f.project_id
          WHERE f.archived_at IS NULL
            AND (f.design_id IS NULL OR f.design_id = p.active_design_id)
          GROUP BY f.project_id`,
      ),
    );

    /* Priced SCOPE: each work item's own live lines, summed per item, and
     * only the items that come to more than nothing. */
    const priced = byProject(
      await this.db.select<{ project_id: string; n: number; cents: number; first_at: string | null }>(
        `SELECT project_id, COUNT(*) AS n, SUM(cents) AS cents, MIN(first_at) AS first_at
           FROM (
             SELECT f.project_id, f.id, SUM(l.amount_cents) AS cents, MIN(l.created_at) AS first_at
               FROM project_features f
               JOIN projects p ON p.id = f.project_id
               JOIN proposed_cost_lines l ON l.feature_id = f.id AND l.archived_at IS NULL
              WHERE f.archived_at IS NULL
                AND (f.design_id IS NULL OR f.design_id = p.active_design_id)
              GROUP BY f.project_id, f.id
             HAVING SUM(l.amount_cents) > 0
           )
          GROUP BY project_id`,
      ),
    );

    const estimate = byProject(
      await this.db.select<{ project_id: string; cents: number }>(
        `SELECT l.project_id, COALESCE(SUM(l.amount_cents), 0) AS cents
           FROM proposed_cost_lines l
           JOIN projects p ON p.id = l.project_id
           LEFT JOIN project_features f ON f.id = l.feature_id
          WHERE l.archived_at IS NULL
            AND (l.feature_id IS NULL
                 OR (f.archived_at IS NULL AND (f.design_id IS NULL OR f.design_id = p.active_design_id)))
          GROUP BY l.project_id`,
      ),
    );

    const bids = byProject(
      await this.db.select<{ project_id: string; packages: number; quotes: number; first_at: string | null }>(
        `SELECT project_id, SUM(packages) AS packages, SUM(quotes) AS quotes, MIN(first_at) AS first_at
           FROM (
             SELECT project_id, COUNT(*) AS packages, 0 AS quotes, MIN(created_at) AS first_at
               FROM bid_packages GROUP BY project_id
             UNION ALL
             SELECT project_id, 0, COUNT(*), MIN(created_at)
               FROM contractor_quotes WHERE archived_at IS NULL GROUP BY project_id
           )
          GROUP BY project_id`,
      ),
    );

    const actuals = byProject(
      await this.db.select<{ project_id: string; n: number; first_at: string | null }>(
        `SELECT project_id, SUM(n) AS n, MIN(first_at) AS first_at
           FROM (
             SELECT project_id, COUNT(*) AS n, MIN(created_at) AS first_at
               FROM actual_expenses WHERE voided_at IS NULL GROUP BY project_id
             UNION ALL
             SELECT project_id, COUNT(*), MIN(created_at) FROM purchase_allocations GROUP BY project_id
             UNION ALL
             SELECT project_id, COUNT(*), MIN(created_at)
               FROM mileage_entries WHERE voided_at IS NULL GROUP BY project_id
           )
          GROUP BY project_id`,
      ),
    );

    const n = (value: number | null | undefined) => Number(value ?? 0);
    return rows.map((p) => ({
      projectId: p.id,
      projectName: p.name,
      projectCreatedAt: p.created_at,
      updatedAt: p.updated_at,
      hasAddress: (p.street_address ?? "").trim() !== "",
      hasPurchasePrice: p.purchase_price_cents !== null,
      hasLivingArea: p.living_area_sqft_x100 !== null,
      roomCount: n(rooms.get(p.id)?.n),
      firstRoomAt: rooms.get(p.id)?.first_at ?? null,
      measuredRoomCount: n(rooms.get(p.id)?.measured),
      wallCount: n(walls.get(p.id)?.n),
      firstWallAt: walls.get(p.id)?.first_at ?? null,
      scopeItemCount: n(scope.get(p.id)?.n),
      firstScopeAt: scope.get(p.id)?.first_at ?? null,
      pricedScopeItemCount: n(priced.get(p.id)?.n),
      pricedScopeCents: n(priced.get(p.id)?.cents),
      firstPricedAt: priced.get(p.id)?.first_at ?? null,
      estimateCents: n(estimate.get(p.id)?.cents),
      bidPackageCount: n(bids.get(p.id)?.packages),
      quoteCount: n(bids.get(p.id)?.quotes),
      firstBidAt: bids.get(p.id)?.first_at ?? null,
      actualEventCount: n(actuals.get(p.id)?.n),
      firstActualAt: actuals.get(p.id)?.first_at ?? null,
    }));
  }
}
